import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toBlob } from "html-to-image";
import { Download, Link2, Share2 } from "lucide-react";

import { api, track } from "@/lib/api";
import { BrandMark } from "@/components/cards";
import { Button, CheckRow, Chip, ScreenHeader, Spinner } from "@/components/kit";
import { Page } from "@/components/layout";
import { useApp } from "@/store";

export default function Share() {
  const [params] = useSearchParams();
  const { recs, profile, tryOn, toast } = useApp();
  const navigate = useNavigate();
  const [slug, setSlug] = useState(params.get("slug") ?? recs?.[0]?.style.slug);
  const [style, setStyle] = useState(null);
  const [match, setMatch] = useState(null);
  const [includeTryOn, setIncludeTryOn] = useState(false);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const cardRef = useRef(null);
  const canShareFiles = typeof navigator !== "undefined" && !!navigator.canShare;

  useEffect(() => {
    if (!slug && recs?.[0]) setSlug(recs[0].style.slug);
  }, [slug, recs]);
  useEffect(() => {
    if (!slug) return;
    let alive = true;
    setError(null);
    api(`/hairstyles/${slug}/match`, { method: "POST", body: { profile } })
      .then((r) => {
        if (!alive) return;
        setStyle(r.style);
        setMatch(r.match);
      })
      .catch((e) => alive && setError(e.message));
    return () => {
      alive = false;
    };
  }, [slug, profile]);

  const canUseTryOn = !!tryOn && tryOn.slug === slug;
  const showTryOn = canUseTryOn && includeTryOn;
  const choices = [...(recs ?? []).map((r) => r.style)];
  if (style && !choices.find((c) => c.slug === style.slug)) choices.unshift(style);
  const link = `${window.location.origin}/style/${slug}`;

  const capture = async () => {
    const blob = await toBlob(cardRef.current, { pixelRatio: 2, cacheBust: true });
    if (!blob) throw new Error("Couldn't create the image.");
    return new File([blob], `breez-match-${slug}.png`, { type: "image/png" });
  };
  const download = async () => {
    setBusy("save");
    try {
      const file = await capture();
      const url = URL.createObjectURL(file);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast("Share card downloaded.", "success");
    } catch (e) {
      toast(e.message ?? "Couldn't create the image.", "error");
    } finally {
      setBusy(null);
    }
  };
  const share = async () => {
    setBusy("share");
    try {
      const file = await capture();
      const data = { files: [file], title: "My BREEZ match", text: `My BREEZ match: ${style.name} — ${link}` };
      if (navigator.canShare?.(data)) {
        await navigator.share(data);
        track("share", { slug: style?.slug });
      } else {
        toast("This browser can't share images directly — use Download image or Copy link.", "info");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast(e.message ?? "Couldn't open the share menu.", "error");
    } finally {
      setBusy(null);
    }
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      track("share", { slug: style?.slug });
      toast("Link copied.", "success");
    } catch {
      toast(`Copy this link: ${link}`, "info");
    }
  };

  return (
    <div data-testid="share-screen">
      <ScreenHeader testId="share-back" label="Share my match" />
      <Page className="flex flex-col gap-4 pb-12">
        {!slug ? (
          <div className="flex flex-col gap-3">
            <p className="t-body">Add your hair profile or open any style to create a share card.</p>
            <Button testId="share-explore" title="Explore styles" onClick={() => navigate("/explore")} />
          </div>
        ) : error ? <p data-testid="share-error" className="t-body">{error}</p> : !style || !match ? <Spinner className="mx-auto mt-8" /> : (
          <div className="grid gap-6 md:grid-cols-[minmax(0,420px)_1fr]">
            <div ref={cardRef} data-testid="share-card" className="relative flex aspect-[4/5] w-full flex-col justify-between overflow-hidden rounded-[20px] bg-navy">
              <img src={showTryOn ? tryOn.image : style.image_url} alt="" crossOrigin="anonymous" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(16,39,72,0.55) 0%, rgba(16,39,72,0) 30%, rgba(16,39,72,0.95) 75%)" }} />
              <div className="relative flex items-center justify-between p-6">
                <BrandMark testId="share-card-wordmark" height={28} className="font-display text-2xl tracking-[6px] text-white" />
                <span className="font-body-bold text-[10px] tracking-[1.4px] text-sky">MY BREEZ MATCH</span>
              </div>
              <div className="relative flex flex-col gap-1 p-6">
                <p data-testid="share-card-style" className="font-display text-[34px] leading-9 text-white">{style.name.toUpperCase()}</p>
                {match.primary ? <p data-testid="share-card-product" className="font-body-semi text-[15px] text-sky">Styled with {match.primary.name}</p> : null}
                {showTryOn ? <p className="mt-1 font-body text-[11px] text-soft">AI-generated preview — actual results may vary.</p> : null}
                <div className="mt-3 flex items-center justify-between border-t border-glass pt-3">
                  <span className="flex-1 font-body-bold text-[9px] tracking-[1.2px] text-soft">YOUR HAIR. YOUR STYLE. YOUR BREEZ.</span>
                  <span className="font-display-bold text-sm text-white">breez.au</span>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-4">
              {choices.length > 1 ? (
                <div className="flex flex-col gap-2">
                  <span className="t-label">Style on the card</span>
                  <div className="flex flex-wrap gap-2">
                    {choices.map((c) => <Chip key={c.slug} testId={`share-style-${c.slug}`} label={c.name} selected={c.slug === slug} onClick={() => { setSlug(c.slug); setIncludeTryOn(false); }} />)}
                  </div>
                </div>
              ) : null}
              {canUseTryOn ? (
                <CheckRow testId="share-include-tryon" checked={includeTryOn} onChange={setIncludeTryOn} label="Use my AI try-on preview (shows my face)" />
              ) : <p className="t-caption">The card uses the style&apos;s reference photo — your face is never included unless you add your own try-on preview.</p>}
              <div className="flex flex-col gap-3 sm:flex-row">
                <Button testId="share-save-image" title="Download image" icon={Download} variant="outline" loading={busy === "save"} onClick={download} className="flex-1" />
                {canShareFiles ? <Button testId="share-open-sheet" title="Share" icon={Share2} loading={busy === "share"} onClick={share} className="flex-1" /> : null}
              </div>
              <Button testId="share-copy-link" title="Copy link" icon={Link2} variant="ghost" onClick={copyLink} />
              <p data-testid="share-honest-note" className="t-caption">
                {canShareFiles ? "Share opens your device's share menu — pick Instagram, WhatsApp, Messages and more." : "Your browser doesn't support sharing images — download the card and post it yourself, or copy the link."} Copy link copies a public link to this style (no photo). BREEZ never posts for you.
              </p>
            </div>
          </div>
        )}
      </Page>
    </div>
  );
}
